import React, { useState } from "react";
import { FileSpreadsheet, Download, Printer, TrendingUp, DollarSign, Wallet, Receipt, PiggyBank, Target } from "lucide-react";
import api, { money } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Cell } from "recharts";
import { toast } from "sonner";

const axisFmt = (v) => (Math.abs(v) >= 1000 ? `Q${(v / 1000).toFixed(1)}k` : `Q${Math.round(v)}`);
const ORANGE = "#F26A00";

const Kpi = ({ icon: Icon, label, value, sub, tone }) => (
  <Card className="rounded-sm border shadow-none p-4" data-testid={`kpi-${label}`}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
      <Icon className={`h-4 w-4 ${tone || "text-primary"}`} />
    </div>
    <div className={`mt-2 text-2xl font-heading font-bold ${tone || ""}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </Card>
);

const GastosCard = ({ title, rows, total, testid, tone }) => (
  <Card className="rounded-sm border shadow-none overflow-hidden" data-testid={testid}>
    <div className="p-4 border-b border-border flex items-center justify-between">
      <span className="font-heading font-bold text-sm">{title}</span>
      <span className={`font-mono font-bold text-sm ${tone || ""}`}>{money("GTQ", total)}</span>
    </div>
    <div className="overflow-x-auto max-h-72 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground sticky top-0">
          <tr><th className="p-2.5 text-left font-bold">Concepto</th><th className="p-2.5 text-left font-bold">Detalle</th><th className="p-2.5 text-right font-bold">Monto</th></tr>
        </thead>
        <tbody>
          {(!rows || rows.length === 0) ? <tr><td colSpan={3} className="p-6 text-center text-muted-foreground text-xs">Sin gastos en el periodo.</td></tr> :
            rows.map((g, i) => (
              <tr key={i} className="border-t border-border/50">
                <td className="p-2.5">{g.concept || "—"}</td>
                <td className="p-2.5 text-muted-foreground text-xs">{g.detail || "—"}</td>
                <td className="p-2.5 text-right font-mono text-xs">{money("GTQ", g.amount)}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  </Card>
);

export default function VentasUtilidad({ data, from, to }) {
  const [busy, setBusy] = useState(false);
  const [inc, setInc] = useState({ repuestos: false, fijo: true, variable: true });
  const k = data.kpis;
  const gRep = k.gastos_repuestos || 0, gFijo = k.gastos_fijos || 0, gVar = k.gastos_variables || 0;
  const considered = Math.round(((inc.repuestos ? gRep : 0) + (inc.fijo ? gFijo : 0) + (inc.variable ? gVar : 0)) * 100) / 100;
  const utilNeta = Math.round((k.utilidad_bruta - considered) * 100) / 100;
  const margenNeto = k.venta_total ? Math.round((utilNeta / k.venta_total) * 1000) / 10 : 0;
  const incKeys = Object.keys(inc).filter((b) => inc[b]);
  const pe = k.punto_equilibrio || 0;
  const cob = pe ? Math.min(100, Math.round((k.venta_total / pe) * 100)) : 0;
  const dif = Math.round((k.venta_total - pe) * 100) / 100;
  const alcanzado = pe > 0 && k.venta_total >= pe;
  const qs = () => { const p = new URLSearchParams(); if (from && to) { p.set("date_from", from); p.set("date_to", to); } p.set("include", incKeys.join(",")); p.set("_t", Date.now()); return `?${p}`; };

  const downloadExcel = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/reports/ventas-utilidad.xlsx${qs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Reporte_Ventas_Utilidad_${from || "mes"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el Excel"); }
    setBusy(false);
  };

  const downloadCsv = () => {
    const head = ["Factura", "Fecha", "Cliente", "Cantidad", "Producto", "Proveedor", "Costo unit", "Costo total", "P. venta", "Total venta", "Utilidad"];
    const lines = [head.join(",")];
    data.invoices.forEach((inv) => inv.lines.forEach((ln) => {
      lines.push([inv.number, inv.date, inv.client, ln.qty, ln.product, ln.supplier, ln.cost_unit, ln.cost_total, ln.sale_unit, ln.sale_total, ln.profit]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    }));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "reporte_ventas_utilidad.csv"; a.click();
  };

  const printReport = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const m = (v) => esc(money("GTQ", v || 0));
    const logo = `${window.location.origin}/logo-selec.jpg`;
    const invHtml = data.invoices.map((inv) => `
      <tr style="background:#f7f7f7"><td colspan="6" style="padding:6px 8px;font-weight:bold">${esc(inv.number)} · ${esc(inv.date)} · ${esc(inv.client)}</td>
      <td style="text-align:right;padding:6px 8px;font-weight:bold">${m(inv.costo)}</td><td></td><td style="text-align:right;padding:6px 8px;font-weight:bold">${m(inv.venta)}</td><td style="text-align:right;padding:6px 8px;font-weight:bold">${m(inv.utilidad)}</td></tr>
      ${inv.lines.map((ln) => `<tr>
        <td style="padding:4px 8px">${esc(ln.qty)}</td><td style="padding:4px 8px">${esc(ln.product)}</td><td style="padding:4px 8px">${esc(ln.supplier)}</td>
        <td style="text-align:right;padding:4px 8px;font-family:monospace">${m(ln.cost_unit)}</td><td style="text-align:right;padding:4px 8px;font-family:monospace">${m(ln.cost_total)}</td>
        <td></td><td style="text-align:right;padding:4px 8px;font-family:monospace">${m(ln.sale_unit)}</td><td></td><td style="text-align:right;padding:4px 8px;font-family:monospace">${m(ln.sale_total)}</td><td style="text-align:right;padding:4px 8px;font-family:monospace">${m(ln.profit)}</td>
      </tr>`).join("")}`).join("");
    const tag = (b) => incKeys.includes(b) ? "" : " (no incluido)";
    const rows = [
      ["Venta total", m(k.venta_total)], ["Costo de ventas", m(k.costo_total)],
      ["Utilidad bruta", m(k.utilidad_bruta) + ` (${k.margen_bruto}%)`],
      [`Compras repuestos${tag("repuestos")}`, m(gRep)],
      [`Gastos fijos${tag("fijo")}`, m(gFijo)],
      [`Gastos variables${tag("variable")}`, m(gVar)],
      ["Gastos considerados", m(considered)],
      ["Utilidad neta", m(utilNeta) + ` (${margenNeto}%)`],
      ["Margen de contribución", m(k.margen_contribucion) + ` (${k.margen_contribucion_pct}%)`],
      ["Punto de equilibrio", m(k.punto_equilibrio)],
    ].map(([l, v]) => `<tr><td style="padding:4px 8px;font-weight:bold">${l}</td><td style="text-align:right;padding:4px 8px;font-family:monospace;font-weight:bold">${v}</td></tr>`).join("");
    const gastosTable = (title, list, total) => `
      <h3 style="color:${ORANGE};margin:16px 0 4px">${title} — ${m(total)}</h3>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <thead><tr style="color:#fff;background:${ORANGE}"><th style="padding:5px 8px;text-align:left">Concepto</th><th style="padding:5px 8px;text-align:left">Detalle</th><th style="padding:5px 8px;text-align:right">Monto</th></tr></thead>
        <tbody>${(list && list.length ? list : []).map((g) => `<tr><td style="padding:4px 8px">${esc(g.concept)}</td><td style="padding:4px 8px">${esc(g.detail)}</td><td style="padding:4px 8px;text-align:right;font-family:monospace">${m(g.amount)}</td></tr>`).join("") || '<tr><td colspan="3" style="padding:8px;color:#888">Sin gastos.</td></tr>'}</tbody>
      </table>`;
    const html = `<html><head><title>Reporte de Ventas y Utilidad</title></head>
      <body style="font-family:Arial,sans-serif;color:#1f2937;padding:24px">
      <div style="display:flex;align-items:center;gap:16px;border-bottom:3px solid ${ORANGE};padding-bottom:10px">
        <img src="${logo}" style="height:52px" onerror="this.style.display='none'"/>
        <div><h2 style="color:${ORANGE};margin:0">SELEC ONE 360</h2>
        <div style="font-size:13px;font-weight:bold">Reporte de Ventas y Utilidad</div>
        <div style="font-size:11px;color:#6b7280">Periodo: ${esc(data.period.label)} · Generado ${esc(new Date().toLocaleString())}</div></div>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:11px;margin-top:12px">
        <thead><tr style="color:#fff;background:${ORANGE}">
        ${["Cant./Fac.", "Producto", "Proveedor", "Costo unit.", "Costo total", "", "P. venta", "", "Total venta", "Utilidad"].map((h) => `<th style="padding:6px 8px;text-align:left">${h}</th>`).join("")}
        </tr></thead><tbody>${invHtml}</tbody></table>
      <h3 style="color:${ORANGE};margin:18px 0 4px">Resumen</h3>
      <table style="border-collapse:collapse;font-size:12px;min-width:320px">${rows}</table>
      ${gastosTable("Compras de repuestos", data.gastos_repuestos, gRep)}
      ${gastosTable("Gastos fijos", data.gastos_fijos, gFijo)}
      ${gastosTable("Gastos variables", data.gastos_variables, gVar)}
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    w.location.href = url;
    setTimeout(() => { w.focus(); w.print(); URL.revokeObjectURL(url); }, 500);
  };

  const clientData = data.top_clients.map((c) => ({ name: c.name.length > 16 ? c.name.slice(0, 15) + "…" : c.name, venta: c.venta, utilidad: c.utilidad }));

  return (
    <div className="space-y-5" data-testid="ventas-utilidad-report">
      <Card className="rounded-sm border shadow-none p-4 flex flex-wrap items-center gap-3">
        <img src={`${window.location.origin}/logo-selec.jpg`} alt="SELEC" className="h-9" onError={(e) => { e.target.style.display = "none"; }} />
        <div className="flex-1 min-w-[180px]">
          <div className="font-heading font-bold text-sm">Reporte de Ventas y Utilidad</div>
          <div className="text-xs text-muted-foreground">Periodo: {data.period.label} · {k.num_facturas} factura(s)</div>
        </div>
        <Button className="rounded-sm h-9 text-xs gap-1 font-bold" onClick={downloadExcel} disabled={busy} data-testid="vu-excel"><FileSpreadsheet className="h-3.5 w-3.5" /> Excel</Button>
        <Button variant="outline" className="rounded-sm h-9 text-xs gap-1" onClick={downloadCsv} data-testid="vu-csv"><Download className="h-3.5 w-3.5" /> CSV</Button>
        <Button variant="outline" className="rounded-sm h-9 text-xs gap-1" onClick={printReport} data-testid="vu-print"><Printer className="h-3.5 w-3.5" /> Imprimir</Button>
      </Card>

      <Card className="rounded-sm border shadow-none p-3 flex flex-wrap items-center gap-4" data-testid="vu-include">
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Restar de la utilidad:</span>
        {[["fijo", "Gastos fijos"], ["variable", "Gastos variables"], ["repuestos", "Compras de repuestos"]].map(([b, l]) => (
          <label key={b} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`inc-${b}`}>
            <input type="checkbox" className="h-4 w-4 accent-primary" checked={inc[b]} onChange={(e) => setInc({ ...inc, [b]: e.target.checked })} />
            {l}
          </label>
        ))}
        {inc.repuestos && <span className="text-xs text-amber-600 font-semibold">⚠ Las compras de repuestos ya están dentro del costo de ventas; incluirlas puede contar doble.</span>}
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi icon={DollarSign} label="Venta total" value={money("GTQ", k.venta_total)} sub={`${k.num_facturas} facturas`} />
        <Kpi icon={Receipt} label="Costo de ventas" value={money("GTQ", k.costo_total)} />
        <Kpi icon={TrendingUp} label="Utilidad bruta" value={money("GTQ", k.utilidad_bruta)} sub={`Margen ${k.margen_bruto}%`} tone="text-emerald-600" />
        <Kpi icon={Wallet} label="Gastos considerados" value={money("GTQ", considered)} sub={`Fijos ${money("GTQ", gFijo)} · Var ${money("GTQ", gVar)}`} />
        <Kpi icon={PiggyBank} label="Utilidad neta" value={money("GTQ", utilNeta)} sub={`Margen ${margenNeto}%`} tone={utilNeta >= 0 ? "text-emerald-600" : "text-destructive"} />
      </div>

      {k.venta_total > 0 && (
        <Card className="rounded-sm border shadow-none overflow-hidden" data-testid="punto-equilibrio-card">
          <div className="p-4 border-b border-border flex flex-wrap items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            <span className="font-heading font-bold text-sm">Punto de equilibrio</span>
            <span className="text-xs text-muted-foreground">Ventas necesarias para cubrir los costos fijos ({money("GTQ", gFijo)})</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
            <div className="bg-card p-4" data-testid="pe-valor">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Punto de equilibrio</div>
              <div className="mt-1 text-2xl font-heading font-bold text-primary">{pe > 0 ? money("GTQ", pe) : "—"}</div>
              <div className="text-xs text-muted-foreground">en ventas</div>
            </div>
            <div className="bg-card p-4">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Margen de contribución</div>
              <div className="mt-1 text-2xl font-heading font-bold">{money("GTQ", k.margen_contribucion)}</div>
              <div className="text-xs text-muted-foreground">{k.margen_contribucion_pct}% de las ventas</div>
            </div>
            <div className="bg-card p-4">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Costos variables</div>
              <div className="mt-1 text-2xl font-heading font-bold">{money("GTQ", k.costos_variables)}</div>
              <div className="text-xs text-muted-foreground">Costo ventas + gastos var.</div>
            </div>
            <div className="bg-card p-4">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Costos fijos</div>
              <div className="mt-1 text-2xl font-heading font-bold">{money("GTQ", gFijo)}</div>
              <div className="text-xs text-muted-foreground">a cubrir</div>
            </div>
          </div>
          <div className="p-4 border-t border-border">
            {pe > 0 ? (
              <>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-semibold">Venta actual: {money("GTQ", k.venta_total)}</span>
                  <span className={alcanzado ? "text-emerald-600 font-bold" : "text-destructive font-bold"} data-testid="pe-estado">
                    {alcanzado ? `Superado por ${money("GTQ", dif)}` : `Faltan ${money("GTQ", -dif)}`}
                  </span>
                </div>
                <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${alcanzado ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${cob}%` }} />
                </div>
                <div className="text-xs text-muted-foreground mt-1.5">
                  {alcanzado
                    ? `Las ventas del periodo cubren los costos fijos (${k.cobertura_pe_pct}% del punto de equilibrio). Cada quetzal adicional aporta ${k.margen_contribucion_pct}% de utilidad.`
                    : `Las ventas cubren el ${k.cobertura_pe_pct}% del punto de equilibrio. Necesitas vender ${money("GTQ", -dif)} más para no perder.`}
                </div>
              </>
            ) : (
              <div className="text-xs text-muted-foreground">No se puede calcular el punto de equilibrio: el margen de contribución no es positivo (los costos variables igualan o superan las ventas).</div>
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-sm border shadow-none p-4">
          <div className="font-heading font-bold text-sm mb-3">Utilidad por cliente (Top 8)</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={clientData} margin={{ left: 4, right: 8, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tickFormatter={axisFmt} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => money("GTQ", v)} />
              <Bar dataKey="venta" name="Venta" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="utilidad" name="Utilidad" radius={[3, 3, 0, 0]}>
                {clientData.map((e, i) => <Cell key={i} fill={e.utilidad >= 0 ? ORANGE : "#ef4444"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="rounded-sm border shadow-none p-4">
          <div className="font-heading font-bold text-sm mb-3">Tendencia diaria</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.trend} margin={{ left: 4, right: 8, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 9 }} />
              <YAxis tickFormatter={axisFmt} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => money("GTQ", v)} />
              <Line type="monotone" dataKey="venta" name="Venta" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="utilidad" name="Utilidad" stroke={ORANGE} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="rounded-sm border shadow-none overflow-hidden">
        <div className="p-4 border-b border-border font-heading font-bold text-sm">Detalle por factura</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vu-table">
            <thead className="bg-primary text-primary-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left font-bold">Cant.</th><th className="p-3 text-left font-bold">Producto</th><th className="p-3 text-left font-bold">Proveedor</th>
                <th className="p-3 text-right font-bold">Costo unit.</th><th className="p-3 text-right font-bold">Costo total</th>
                <th className="p-3 text-right font-bold">P. venta</th><th className="p-3 text-right font-bold">Total venta</th><th className="p-3 text-right font-bold">Utilidad</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.length === 0 ? <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">Sin ventas en el periodo.</td></tr> :
                data.invoices.map((inv) => (
                  <React.Fragment key={inv.number}>
                    <tr className="bg-muted/60 border-t border-border">
                      <td colSpan={3} className="p-2.5 font-bold text-primary">{inv.number} · {inv.date} · {inv.client}{!inv.costed && <span className="ml-2 text-[10px] text-amber-600 font-semibold">(sin costear)</span>}</td>
                      <td className="p-2.5 text-right font-mono font-bold">{money("GTQ", inv.costo)}</td><td></td>
                      <td className="p-2.5 text-right font-mono font-bold">{money("GTQ", inv.venta)}</td>
                      <td className="p-2.5 text-right font-mono font-bold text-emerald-600">{money("GTQ", inv.utilidad)}</td>
                    </tr>
                    {inv.lines.map((ln, i) => (
                      <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
                        <td className="p-2.5">{ln.qty}</td><td className="p-2.5">{ln.product}</td><td className="p-2.5 text-muted-foreground text-xs">{ln.supplier || "—"}</td>
                        <td className="p-2.5 text-right font-mono text-xs">{money("GTQ", ln.cost_unit)}</td><td className="p-2.5 text-right font-mono text-xs">{money("GTQ", ln.cost_total)}</td>
                        <td className="p-2.5 text-right font-mono text-xs">{money("GTQ", ln.sale_unit)}</td><td className="p-2.5 text-right font-mono text-xs">{money("GTQ", ln.sale_total)}</td>
                        <td className="p-2.5 text-right font-mono text-xs">{money("GTQ", ln.profit)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GastosCard title="Compras de repuestos" rows={data.gastos_repuestos} total={gRep} testid="gastos-repuestos-card" tone="text-muted-foreground" />
        <GastosCard title="Gastos fijos" rows={data.gastos_fijos} total={gFijo} testid="gastos-fijos-card" />
        <GastosCard title="Gastos variables" rows={data.gastos_variables} total={gVar} testid="gastos-variables-card" />
      </div>
    </div>
  );
}
